import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";

export default function AdminMfaSetup() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [provision, setProvision] = useState(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ["admin-mfa-status"],
    queryFn: async () => (await api.get("/admin-mfa/status")).data,
  });

  useEffect(() => {
    if (!status || status.enrolled || provision) return;
    api.post("/admin-mfa/enroll/start").then((r) => setProvision(r.data)).catch((e) => {
      setError(e?.response?.data?.detail || "Could not start MFA enrollment.");
    });
  }, [status, provision, api]);

  async function submitEnrollment() {
    setError("");
    if (!code.trim()) return setError("Enter the 6-digit code from your authenticator app.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin-mfa/enroll/verify", { code: code.trim() });
      setRecoveryCodes(data.recovery_codes);
      toast.success("Authenticator verified.");
    } catch (e) {
      setError(e?.response?.data?.detail || "That code didn't match. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function submitChallenge() {
    setError("");
    if (!code.trim()) return setError(useRecovery ? "Enter a recovery code." : "Enter the 6-digit code from your authenticator app.");
    setBusy(true);
    try {
      const path = useRecovery ? "/admin-mfa/challenge/recovery" : "/admin-mfa/challenge/verify";
      await api.post(path, { code: code.trim() });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      await queryClient.invalidateQueries({ queryKey: ["admin-mfa-status"] });
      toast.success("Verified.");
    } catch (e) {
      setError(e?.response?.data?.detail || "That code didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function finishEnrollment() {
    setBusy(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      await queryClient.invalidateQueries({ queryKey: ["admin-mfa-status"] });
    } finally {
      setSaved(true);
      setBusy(false);
    }
  }

  if (isLoading || (!status?.enrolled && !provision)) return <div className="min-h-[60vh]" data-testid="admin-mfa-loading" />;

  // Already enrolled, but this session hasn't passed a challenge yet.
  if (status.enrolled && !saved) {
    return (
      <div className="max-w-lg mx-auto px-6 py-16" data-testid="admin-mfa-challenge">
        <div className="flex items-center gap-3 mb-6">
          <KeyRound className="w-6 h-6 text-brandAccent" />
          <h1 className="font-display text-display-xs">Administrator Verification Required</h1>
        </div>
        <p className="text-white/60 text-sm mb-6">
          Enter the 6-digit code from your authenticator app to start this session.
        </p>
        {error && <p className="text-red-400 text-sm mb-4" data-testid="admin-mfa-error">{error}</p>}
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode={useRecovery ? "text" : "numeric"}
          autoComplete="one-time-code"
          placeholder={useRecovery ? "XXXX-XXXX" : "000000"}
          data-testid="admin-mfa-challenge-input"
          className="bg-surface border border-border px-4 py-2.5 w-full text-lg tracking-[0.2em] mb-4"
        />
        <button
          onClick={submitChallenge}
          disabled={busy}
          data-testid="admin-mfa-challenge-submit"
          className="bg-foreground text-background px-5 py-2.5 text-sm uppercase tracking-wide disabled:opacity-50 w-full mb-3"
        >
          {busy ? "Verifying…" : "Verify"}
        </button>
        <button
          onClick={() => { setUseRecovery((v) => !v); setCode(""); setError(""); }}
          data-testid="admin-mfa-toggle-recovery"
          className="text-xs text-white/50 underline underline-offset-2"
        >
          {useRecovery ? "Use authenticator code instead" : "Use a backup recovery code instead"}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-6 py-16" data-testid="admin-mfa-setup">
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck className="w-6 h-6 text-brandAccent" />
        <h1 className="font-display text-display-xs">Administrator MFA Required</h1>
      </div>
      <p className="text-white/60 text-sm mb-8">
        Endure Beyond requires every administrator to enroll an authenticator app before accessing the
        Administration Studio. This is mandatory and cannot be skipped.
      </p>

      {error && <p className="text-red-400 text-sm mb-4" data-testid="admin-mfa-error">{error}</p>}

      {recoveryCodes.length === 0 && provision && (
        <div className="space-y-5">
          <div>
            <p className="text-xs uppercase tracking-widest text-white/70 mb-3">1. Scan with your authenticator app</p>
            <div className="bg-white p-4 inline-block" data-testid="admin-mfa-qr">
              <QRCodeSVG value={provision.otpauth_uri} size={200} />
            </div>
            <p className="text-sm text-white/70 mt-3 break-all">Or enter manually: <span className="font-mono text-white">{provision.secret}</span></p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-white/70 mb-3">2. Enter the 6-digit code</p>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              data-testid="admin-mfa-code-input"
              className="bg-surface border border-border px-4 py-2.5 w-full text-lg tracking-[0.3em]"
            />
          </div>
          <button
            onClick={submitEnrollment}
            disabled={busy}
            data-testid="admin-mfa-verify-button"
            className="bg-foreground text-background px-5 py-2.5 text-sm uppercase tracking-wide disabled:opacity-50"
          >
            {busy ? "Verifying…" : "Verify & Generate Backup Codes"}
          </button>
        </div>
      )}

      {recoveryCodes.length > 0 && !saved && (
        <div className="space-y-4" data-testid="admin-mfa-backup-codes">
          <p className="text-sm font-semibold">Save these recovery codes now — they will not be shown again:</p>
          <pre className="bg-surface border border-border p-4 text-sm leading-7">{recoveryCodes.join("\n")}</pre>
          <button
            onClick={finishEnrollment}
            disabled={busy}
            data-testid="admin-mfa-confirm-saved-button"
            className="bg-foreground text-background px-5 py-2.5 text-sm uppercase tracking-wide disabled:opacity-50"
          >
            {busy ? "Finishing…" : "I've Saved My Recovery Codes"}
          </button>
        </div>
      )}

      {saved && (
        <p className="text-white/60 text-sm" data-testid="admin-mfa-done-message">
          MFA enrolled. Loading the Administration Studio…
        </p>
      )}
    </div>
  );
}
